// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { Search, Download, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ScoutEarning {
  id: string;
  scout_id: string;
  scout_name: string;
  athlete_name: string;
  athlete_username: string;
  membership_slug: string;
  membership_name: string;
  membership_price_cents: number;
  scout_revenue_percentage: number;
  scout_earning: number;
  created_at: string;
  status: 'estimated' | 'paid';
}

const ScoutFinanceTab = () => {
  const [earnings, setEarnings] = useState<ScoutEarning[]>([]);
  const [filteredEarnings, setFilteredEarnings] = useState<ScoutEarning[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [scoutFilter, setScoutFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState({ start: undefined as Date | undefined, end: undefined as Date | undefined });
  const [isLoading, setIsLoading] = useState(true);
  const [scouts, setScouts] = useState<Array<{ id: string; name: string }>>([]);
  const { toast } = useToast();

  useEffect(() => {
    fetchScoutEarnings();
    fetchScouts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [earnings, searchTerm, statusFilter, scoutFilter, dateRange]);

  const fetchScouts = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name')
        .eq('role', 'scout')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setScouts(data || []);
    } catch (error) {
      console.error('Error fetching scouts:', error);
    }
  };

  const fetchScoutEarnings = async () => {
    try {
      setIsLoading(true);
      
      // Get all active profile memberships with related data
      const { data: profileMemberships, error: membershipError } = await supabase
        .from('profile_memberships')
        .select(`
          id,
          profile_id,
          membership_slug,
          assigned_at,
          profiles!profile_memberships_profile_id_fkey(name, username),
          memberships!inner(slug, name, price_cents)
        `)
        .eq('is_active', true);

      if (membershipError) throw membershipError;

      // Get all scout membership assignments
      const { data: scoutAssignments, error: assignmentError } = await supabase
        .from('scout_membership_assignments')
        .select(`
          scout_id,
          membership_id,
          profiles!inner(id, name, revenue_percentage),
          memberships!inner(slug)
        `);

      if (assignmentError) throw assignmentError;

      // Create scout earnings data
      const scoutEarningsData: ScoutEarning[] = [];

      profileMemberships?.forEach(pm => {
        // Find scouts who have commission assignments for this membership
        const eligibleScouts = scoutAssignments?.filter(sa => {
          const membershipData = Array.isArray(sa.memberships) ? sa.memberships[0] : sa.memberships;
          return membershipData?.slug === pm.membership_slug;
        }) || [];

        eligibleScouts.forEach(scout => {
          const scoutProfile = Array.isArray(scout.profiles) ? scout.profiles[0] : scout.profiles;
          const membershipData = Array.isArray(pm.memberships) ? pm.memberships[0] : pm.memberships;
          const athleteProfile = Array.isArray(pm.profiles) ? pm.profiles[0] : pm.profiles;

          if (scoutProfile && membershipData && athleteProfile) {
            const membershipPrice = membershipData.price_cents / 100;
            const scoutEarning = membershipPrice * (scoutProfile.revenue_percentage / 100);

            scoutEarningsData.push({
              id: `${scout.scout_id}-${pm.id}`,
              scout_id: scout.scout_id,
              scout_name: scoutProfile.name,
              athlete_name: athleteProfile.name,
              athlete_username: athleteProfile.username,
              membership_slug: pm.membership_slug,
              membership_name: membershipData.name,
              membership_price_cents: membershipData.price_cents,
              scout_revenue_percentage: scoutProfile.revenue_percentage,
              scout_earning: Math.round(scoutEarning * 100) / 100,
              created_at: pm.assigned_at,
              status: 'estimated' // Default to estimated since we don't have payout records yet
            });
          }
        });
      });

      setEarnings(scoutEarningsData);
    } catch (error) {
      console.error('Error fetching scout earnings:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load scout earnings data"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...earnings];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(earning =>
        earning.scout_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        earning.athlete_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        earning.membership_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(earning => earning.status === statusFilter);
    }

    // Scout filter
    if (scoutFilter !== 'all') {
      filtered = filtered.filter(earning => earning.scout_id === scoutFilter);
    }

    // Date range filter
    if (dateRange.start) {
      filtered = filtered.filter(earning => 
        new Date(earning.created_at) >= dateRange.start!
      );
    }
    if (dateRange.end) {
      filtered = filtered.filter(earning => 
        new Date(earning.created_at) <= dateRange.end!
      );
    }

    setFilteredEarnings(filtered);
  };

  const exportToCSV = () => {
    const headers = ['Scout Name', 'Athlete Name', 'Membership Type', 'Price', 'Commission Rate', 'Scout Earning', 'Status', 'Date'];
    const csvData = filteredEarnings.map(earning => [
      earning.scout_name,
      earning.athlete_name,
      earning.membership_name,
      `$${(earning.membership_price_cents / 100).toFixed(2)}`,
      `${earning.scout_revenue_percentage}%`,
      `$${earning.scout_earning.toFixed(2)}`,
      earning.status,
      new Date(earning.created_at).toLocaleDateString()
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scout-earnings-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalEarnings = filteredEarnings.reduce((sum, earning) => sum + earning.scout_earning, 0);
  const totalMemberships = filteredEarnings.length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Scout Finance & Commission Tracking</CardTitle>
              <CardDescription>
                Track scout commission earnings from membership sales
              </CardDescription>
            </div>
            <Button onClick={exportToCSV} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-green-600">${totalEarnings.toFixed(2)}</div>
                <p className="text-sm text-muted-foreground">Total Estimated Earnings</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-blue-600">{totalMemberships}</div>
                <p className="text-sm text-muted-foreground">Commission Transactions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-purple-600">{scouts.length}</div>
                <p className="text-sm text-muted-foreground">Active Scouts</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search scouts, athletes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="estimated">Estimated</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>

            <Select value={scoutFilter} onValueChange={setScoutFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by scout" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Scouts</SelectItem>
                {scouts.map(scout => (
                  <SelectItem key={scout.id} value={scout.id}>
                    {scout.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DatePicker
              date={dateRange.start}
              setDate={(date) => setDateRange({ ...dateRange, start: date })}
              placeholderText="Start date"
            />

            <DatePicker
              date={dateRange.end}
              setDate={(date) => setDateRange({ ...dateRange, end: date })}
              placeholderText="End date"
            />
          </div>

          {/* Earnings Table */}
          {isLoading ? (
            <div className="text-center py-8">Loading scout earnings...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scout Name</TableHead>
                  <TableHead>Athlete Name</TableHead>
                  <TableHead>Membership</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Commission Rate</TableHead>
                  <TableHead>Scout Earning</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEarnings.map((earning) => (
                  <TableRow key={earning.id}>
                    <TableCell className="font-medium">{earning.scout_name}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{earning.athlete_name}</p>
                        <p className="text-sm text-muted-foreground">@{earning.athlete_username}</p>
                      </div>
                    </TableCell>
                    <TableCell>{earning.membership_name}</TableCell>
                    <TableCell>${(earning.membership_price_cents / 100).toFixed(2)}</TableCell>
                    <TableCell>{earning.scout_revenue_percentage}%</TableCell>
                    <TableCell className="font-medium text-green-600">
                      ${earning.scout_earning.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={earning.status === 'paid' ? 'default' : 'secondary'}>
                        {earning.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(earning.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {filteredEarnings.length === 0 && !isLoading && (
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold mb-2">No earnings found</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all' || scoutFilter !== 'all' 
                  ? 'Try adjusting your search terms or filters' 
                  : 'No scout earnings have been recorded yet'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ScoutFinanceTab;
